import { not } from "ramda";
import React, { type ReactNode, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { EventFilters, PresetFilters, PresetFiltersList } from "../components/Filters/consts";
import { getEventFiltersSearchParams } from "../components/Filters/helpers";
import { useGetEventFiltersFromUrl } from "../components/Filters/hooks";
import { useUnlockedEvents } from "./UnlockedEventsContext";

/**
 * Helpers
 */
export const detectActivePresetFilter = (
  currentFilters: EventFilters["events"],
): PresetFilters | null => {
  for (const presetFilter of PresetFiltersList) {
    const preset = presetFilter.filters;

    const matches =
      preset.type === currentFilters.type &&
      preset.host === currentFilters.host &&
      preset.englishLevel === currentFilters.englishLevel &&
      preset.showFullEvents === currentFilters.showFullEvents &&
      preset.speakingEvents === currentFilters.speakingEvents &&
      preset.topicFilter === currentFilters.topicFilter &&
      preset.eventDate === currentFilters.eventDate &&
      preset.topicId === currentFilters.topicId &&
      preset.groupStudy === currentFilters.groupStudy &&
      JSON.stringify([...(preset.interests ?? [])].sort()) ===
        JSON.stringify([...(currentFilters.interests ?? [])].sort()) &&
      JSON.stringify([...(preset.englishSkills ?? [])].sort()) ===
        JSON.stringify([...(currentFilters.englishSkills ?? [])].sort()) &&
      JSON.stringify([...(preset.occupations ?? [])].sort()) ===
        JSON.stringify([...(currentFilters.occupations ?? [])].sort()) &&
      JSON.stringify([...(preset.duration ?? [])].sort()) ===
        JSON.stringify([...(currentFilters.duration ?? [])].sort());

    if (matches) {
      return presetFilter.type;
    }
  }

  return null;
};

export const getDefaultEventFiltersForGuestPass = (
  currentFilters: EventFilters["events"],
  isGuestPassUser: boolean,
): EventFilters["events"] => {
  if (!isGuestPassUser || detectActivePresetFilter(currentFilters) !== PresetFilters.ShowAll) {
    return currentFilters;
  }

  const newMemberFilters = PresetFiltersList.find(
    (preset) => preset.type === PresetFilters.NMEvents,
  )?.filters;
  if (!newMemberFilters) {
    return currentFilters;
  }

  return {
    ...newMemberFilters,
    eventDate: currentFilters.eventDate,
    eventTime: currentFilters.eventTime,
    customTimeFrom: currentFilters.customTimeFrom,
    customTimeUntil: currentFilters.customTimeUntil,
  };
};

/**
 * Types
 */
interface Props {
  children: JSX.Element | JSX.Element[] | ReactNode | ReactNode[];
}

export interface ContextType {
  filters: EventFilters;
  setFilters: (filters: Partial<EventFilters>) => void;
  searchParams: string;
  activePresetFilterType: PresetFilters | null;
  setActivePresetFilterType: (type: PresetFilters | null) => void;
}

/**
 * Context
 */
const EventsFiltersContext: React.Context<ContextType> = React.createContext({} as ContextType);

/**
 * Provider
 */
export const EventsFiltersProvider: React.FC<Props> = ({ children }: Props) => {
  const eventFiltersFromUrl = useGetEventFiltersFromUrl();
  const { isGuestPassUser } = useUnlockedEvents();
  const defaultEventFilters = getDefaultEventFiltersForGuestPass(
    eventFiltersFromUrl,
    isGuestPassUser,
  );

  const filters = {
    events: defaultEventFilters,
  };

  const [currentSearchParams] = useSearchParams();
  const searchParams = getEventFiltersSearchParams(currentSearchParams, filters);

  const navigate = useNavigate();
  const location = useLocation();
  const setFilters = (filters: EventFilters) => {
    const searchParams = getEventFiltersSearchParams(new URLSearchParams(), filters);
    // Replace, so "back" leaves the list instead of undoing filters one at a time
    navigate(
      { pathname: "/events/all", search: searchParams },
      { replace: location.pathname.startsWith("/events/all") },
    );
  };

  const initialPresetFilter =
    detectActivePresetFilter(defaultEventFilters) ?? PresetFilters.ShowAll;
  const [activePresetFilterType, setActivePresetFilterType] = useState<PresetFilters | null>(
    initialPresetFilter,
  );
  useEffect(() => {
    if (location.pathname.includes("/events/all")) {
      const detectedPreset = detectActivePresetFilter(defaultEventFilters) ?? PresetFilters.ShowAll;
      setActivePresetFilterType(detectedPreset);
    } else if (not(location.pathname.includes("/event/"))) {
      setActivePresetFilterType(null);
    }
  }, [location.pathname, defaultEventFilters]);

  return (
    <EventsFiltersContext.Provider
      value={{
        filters,
        setFilters,
        searchParams,
        activePresetFilterType,
        setActivePresetFilterType,
      }}
    >
      {children}
    </EventsFiltersContext.Provider>
  );
};

export default EventsFiltersContext;
